import { useCallback, useState } from 'react';
import { noteIsBlank } from '../records/noteText';

/**
 * The Add/Edit Note modal (piece 5; decisions 0175, 0204). Every record's
 * Internal Notes uses this one modal, and no other screen in the plan edits a
 * Note — so the four optional columns have their writer here or nowhere.
 *
 * Author and dateTime are not in the draft at all. They are never entered by
 * the person and the server sets both on save; a field for either would be a
 * second source of truth for a fact the server owns.
 */
export type NoteDirection = 'Inbound' | 'Outbound';

/** A further record the note is also about: a `NoteRecordLink` row, with the label the chip shows (decision 0339). */
export interface NoteLink {
  recordType: string;
  recordId: string;
  label: string;
}

export interface NoteDraft {
  /** Free text, required: plain words, or rich text under the notes' allowance (decision 7180). */
  body: string;
  /** The record the note is written on, fixed by the page and never picked (decision 0339). */
  taggedRecordType: string | null;
  taggedRecordId: string | null;
  /** The records the note is also about; the origin is never among them. */
  links: NoteLink[];
  issueFlagId: string | null;
  activityKindId: string | null;
  direction: NoteDirection | null;
  durationMinutes: number | null;
  confidentialityRoleId: string | null;
}

export const emptyNote = (): NoteDraft => ({
  body: '',
  taggedRecordType: null,
  taggedRecordId: null,
  links: [],
  issueFlagId: null,
  activityKindId: null,
  direction: null,
  durationMinutes: null,
  confidentialityRoleId: null,
});

/** The seeded ActivityKind whose choice reveals Direction and Duration. */
export const CALL_ACTIVITY_KIND = 'Call';

/** Direction and Duration are revealed only when the chosen kind is Call. */
export function revealsCallFields(activityKindName: string | null | undefined): boolean {
  return activityKindName === CALL_ACTIVITY_KIND;
}

export interface NoteProblem {
  field: keyof NoteDraft;
  message: 'required';
}

export function noteProblems(draft: NoteDraft): NoteProblem[] {
  return noteIsBlank(draft.body) ? [{ field: 'body', message: 'required' }] : [];
}

/**
 * What the save writes. A Direction or a Duration typed under Call and then
 * abandoned for Meeting is dropped, because the two columns are only ever a
 * question about a call.
 */
export function noteSaveShape(draft: NoteDraft, activityKindName: string | null | undefined): NoteDraft {
  if (revealsCallFields(activityKindName)) return { ...draft };
  return { ...draft, direction: null, durationMinutes: null };
}

/**
 * A flagged note propagates two ways: a banner on the record and a
 * notification to the targets the record already notifies. A run producing
 * only one of the two is the defect — a flag that only notifies is buried in
 * the Notes list, and the person who opens the record next was not necessarily
 * a notification target at all.
 */
export interface FlagPropagation {
  banner: boolean;
  notification: boolean;
}

export function flagPropagation(draft: NoteDraft): FlagPropagation {
  const flagged = draft.issueFlagId !== null;
  return { banner: flagged, notification: flagged };
}

/**
 * The flag notification introduces no second subscription mechanism: phase 1
 * raises the event and names no recipient, and the Overdue and Reminder
 * Engine — stubbed here, built in Files, versions, sharing, audit — decides
 * who hears about it.
 */
export interface FlagEvent {
  recordType: string;
  recordId: string;
  issueFlagId: string;
}

export type RaiseFlagEvent = (event: FlagEvent) => void;

export interface NoteModalState {
  draft: NoteDraft;
  problems: NoteProblem[];
  showsCallFields: boolean;
  set: <K extends keyof NoteDraft>(field: K, value: NoteDraft[K]) => void;
  reset: (to?: NoteDraft) => void;
}

export function useNoteModal(initial: NoteDraft = emptyNote(), activityKindName: string | null = null): NoteModalState {
  const [draft, setDraft] = useState<NoteDraft>(initial);
  const set = useCallback(<K extends keyof NoteDraft>(field: K, value: NoteDraft[K]) => {
    setDraft((d) => ({ ...d, [field]: value }));
  }, []);
  const reset = useCallback((to: NoteDraft = emptyNote()) => setDraft(to), []);
  return { draft, problems: noteProblems(draft), showsCallFields: revealsCallFields(activityKindName), set, reset };
}

/** A link is added once, and never to the record the note is written on. */
export function addNoteLink(draft: NoteDraft, link: NoteLink): NoteDraft {
  if (link.recordType === draft.taggedRecordType && link.recordId === draft.taggedRecordId) return draft;
  if (draft.links.some((l) => l.recordType === link.recordType && l.recordId === link.recordId)) return draft;
  return { ...draft, links: [...draft.links, link] };
}

export function removeNoteLink(draft: NoteDraft, recordType: string, recordId: string): NoteDraft {
  return { ...draft, links: draft.links.filter((l) => !(l.recordType === recordType && l.recordId === recordId)) };
}
